/**
 * NMS Search Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Handles search functionality with PredictiveSearch integration
 */

class NMSSearch extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];
  #placeholderAnimation = null;
  #placeholderTexts = [];
  #currentPlaceholderIndex = 0;

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.searchId = element.dataset.searchId || element.id || `search-${Date.now()}`; // REQUIRED: Unique identifier

    // Load animated placeholder texts from data attribute
    this.#loadPlaceholderTexts();

    // REQUIRED: Store in global registry for external access
    window.neemans.search = window.neemans.search || {};
    window.neemans.search[this.searchId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#setupSearchRefs();
    this.#initializeSearchState();
    this.#bindSearchEvents();
    this.#setupMobileFocus();

    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupSearchRefs() {
    // Query only within component scope using data attributes
    this.refs.input = this.element.querySelector('[data-ref="input"]');
    this.refs.results = this.element.querySelector('[data-ref="results"]');
    this.refs.form = this.element.querySelector('[data-ref="form"]');
    this.refs.clearButton = this.element.querySelector('[data-ref="clear-button"]');

    // Modal-related elements
    this.refs.searchControl = this.element.querySelector('[data-search-control]');
    this.refs.searchWrapper = this.element.querySelector('[data-search-wrapper]');
    this.refs.searchContainer = this.element.querySelector('[data-search-container]');
    this.refs.searchOverlay = this.element.querySelector('[data-search-overlay]');
    this.refs.searchClose = this.element.querySelector('[data-search-close]');
    this.refs.searchEmpty = this.element.querySelector('[data-search-empty]');

    // Animated placeholder elements
    this.refs.placeholderContainer = this.element.querySelector('.nms-search__animated-placeholder');
    this.refs.placeholderText = this.element.querySelector('.nms-search__placeholder-text');
  }

  #initializeSearchState() {
    if (!this.refs.input) {
      console.warn('NMSSearch: No input element found');
      return;
    }

    // Set up global data in window.neemans namespace
    this.setGlobalData(`search.${this.searchId}`, {
      isOpen: false,
      isModalOpen: false,
      query: '',
      results: null,
      isLoading: false,
    });

    // Initialize modal as closed
    this.#setModalState(false);

    // Initialize empty state as visible (no search results initially)
    this.#showEmptyState();

    // Set up mutation observer for predictive search results
    this.#setupResultsObserver();

    // Initialize animated placeholder
    this.#initializeAnimatedPlaceholder();
  }

  #setupResultsObserver() {
    if (!this.refs.results) return;

    // Create mutation observer to watch for predictive search results changes
    this.resultsObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          this.#checkForResults();
        }
      });
    });

    // Start observing the results container
    this.resultsObserver.observe(this.refs.results, {
      childList: true,
      subtree: true
    });
  }

  #checkForResults() {
    if (!this.refs.results || !this.refs.searchEmpty) return;

    // Check if the results container has any meaningful content
    const resultsContent = this.refs.results.querySelector('[slot="results"]');

    if (resultsContent && resultsContent.children.length > 0) {
      // Check if server rendered "no results" state (nms-empty-search-result inside results)
      const serverEmptyState = resultsContent.querySelector('.nms-empty-search-result');
      const noResultsState = resultsContent.querySelector('.nms-search-empty-state__no-results');

      if (serverEmptyState || noResultsState) {
        // Server rendered "no results" - hide our bottom empty state
        this.refs.searchEmpty.style.display = 'none';
        this.refs.searchEmpty.setAttribute('aria-hidden', 'true');
        return;
      }

      // Check if there are actual search results
      const hasResults = resultsContent.querySelector('.predictive-search__results');
      const hasProductResults = resultsContent.querySelector('.predictive-search__products');
      const hasCollectionResults = resultsContent.querySelector('.predictive-search__collections');
      const hasArticleResults = resultsContent.querySelector('.predictive-search__blog-posts');
      const hasPageResults = resultsContent.querySelector('.predictive-search__pages');
      const hasSuggestions = resultsContent.querySelector('.predictive-search__suggestions');

      if (hasResults && (hasProductResults || hasCollectionResults || hasArticleResults || hasPageResults || hasSuggestions)) {
        // Hide empty state when we have actual results
        this.refs.searchEmpty.style.display = 'none';
        this.refs.searchEmpty.setAttribute('aria-hidden', 'true');
      } else {
        // Show bottom empty state when no server content and no results
        this.refs.searchEmpty.style.display = 'block';
        this.refs.searchEmpty.setAttribute('aria-hidden', 'false');
      }
    } else {
      // No results content at all, show bottom empty state (recent searches, trending, etc.)
      this.refs.searchEmpty.style.display = 'block';
      this.refs.searchEmpty.setAttribute('aria-hidden', 'false');
    }
  }

  #bindSearchEvents() {
    // Form submission
    if (this.refs.form) {
      const submitHandler = e => this.#handleSubmit(e);
      this.refs.form.addEventListener('submit', submitHandler);
      this.#eventListeners.push({ element: this.refs.form, event: 'submit', handler: submitHandler });
    }

    // Input events
    if (this.refs.input) {
      const inputHandler = e => this.#handleInput(e);
      const keydownHandler = e => this.#handleKeydown(e);
      const focusHandler = () => this.#handleFocus();
      const blurHandler = () => this.#handleBlur();

      this.refs.input.addEventListener('input', inputHandler);
      this.refs.input.addEventListener('keydown', keydownHandler);
      this.refs.input.addEventListener('focus', focusHandler);
      this.refs.input.addEventListener('blur', blurHandler);

      this.#eventListeners.push(
        { element: this.refs.input, event: 'input', handler: inputHandler },
        { element: this.refs.input, event: 'keydown', handler: keydownHandler },
        { element: this.refs.input, event: 'focus', handler: focusHandler },
        { element: this.refs.input, event: 'blur', handler: blurHandler }
      );
    }

    // Clear button
    if (this.refs.clearButton) {
      const clearHandler = () => this.clearSearch();
      this.refs.clearButton.addEventListener('click', clearHandler);
      this.#eventListeners.push({ element: this.refs.clearButton, event: 'click', handler: clearHandler });
    }

    // Modal events
    this.#bindModalEvents();
  }

  #bindModalEvents() {
    // Search control click - opens modal
    if (this.refs.searchControl) {
      const controlHandler = (e) => {
        e.preventDefault();
        this.openModal();
      };
      this.refs.searchControl.addEventListener('click', controlHandler);
      this.#eventListeners.push({ element: this.refs.searchControl, event: 'click', handler: controlHandler });
    }

    // Close button click - closes modal
    if (this.refs.searchClose) {
      const closeHandler = (e) => {
        e.preventDefault();
        this.closeModal();
      };
      this.refs.searchClose.addEventListener('click', closeHandler);
      this.#eventListeners.push({ element: this.refs.searchClose, event: 'click', handler: closeHandler });
    }

    // Overlay click - closes modal
    if (this.refs.searchOverlay) {
      const overlayHandler = (e) => {
        if (e.target === this.refs.searchOverlay) {
          this.closeModal();
        }
      };
      this.refs.searchOverlay.addEventListener('click', overlayHandler);
      this.#eventListeners.push({ element: this.refs.searchOverlay, event: 'click', handler: overlayHandler });
    }

    // Escape key - closes modal
    const escapeHandler = (e) => {
      if (e.key === 'Escape' && this.getGlobalData(`search.${this.searchId}.isModalOpen`)) {
        this.closeModal();
      }
    };
    document.addEventListener('keydown', escapeHandler);
    this.#eventListeners.push({ element: document, event: 'keydown', handler: escapeHandler });

    // Handle screen resize while modal is open
    const resizeHandler = () => {
      const isModalOpen = this.getGlobalData(`search.${this.searchId}.isModalOpen`);
      if (isModalOpen) {
        this.#handleModalResize();
      }
    };
    window.addEventListener('resize', resizeHandler);
    this.#eventListeners.push({ element: window, event: 'resize', handler: resizeHandler });
  }

  #setupMobileFocus() {
    // Auto-focus on mobile with delay if configured
    const autoFocus = this.element.dataset.autoFocus;

    if (autoFocus === 'mobile' && window.innerWidth <= 768) {
      setTimeout(() => {
        this.refs.input?.focus();
      }, 100);
    }
  }

  #handleSubmit(event) {
    const query = this.refs.input.value.trim();

    if (!query) {
      event.preventDefault();
      this.refs.input.focus();
      return;
    }

    // Update global state
    this.setGlobalData(`search.${this.searchId}.query`, query);

    // Dispatch search submit event
    this.dispatchNMSEvent('search:submit', {
      searchId: this.searchId,
      query: query,
      component: this,
    });
  }

  #handleInput(event) {
    const query = event.target.value;

    // Update global state
    this.setGlobalData(`search.${this.searchId}.query`, query);

    // Show/hide clear button
    this.#toggleClearButton(query.length > 0);

    // Handle animated placeholder based on input value
    if (query.trim() === '') {
      // Input is empty, show placeholder animation
      if (this.refs.placeholderContainer) {
        this.refs.placeholderContainer.style.opacity = '1';
      }
      this.#startPlaceholderAnimation();
    } else {
      // Input has content, hide placeholder animation
      this.#stopPlaceholderAnimation();
      if (this.refs.placeholderContainer) {
        this.refs.placeholderContainer.style.opacity = '0';
      }
    }

    // Handle empty state for input changes
    this.#handleSearchInputChange(query);

    // Dispatch input event for real-time search
    this.dispatchNMSEvent('search:input', {
      searchId: this.searchId,
      query: query,
      component: this,
    });
  }

  #handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeSearch();
    }

    // Arrow key navigation for results
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.#navigateResults(event.key === 'ArrowDown' ? 'down' : 'up');
    }
  }

  #handleFocus() {
    this.setGlobalData(`search.${this.searchId}.isOpen`, true);
    this.element.classList.add('nms-search--focused');

    // Stop placeholder animation when input is focused
    this.#stopPlaceholderAnimation();
    if (this.refs.placeholderContainer) {
      this.refs.placeholderContainer.style.opacity = '0';
    }

    this.dispatchNMSEvent('search:focus', {
      searchId: this.searchId,
      component: this,
    });
  }

  #handleBlur() {
    // Delay to allow clicks on results
    setTimeout(() => {
      this.setGlobalData(`search.${this.searchId}.isOpen`, false);
      this.element.classList.remove('nms-search--focused');

      // Restart placeholder animation when input is blurred (if empty)
      if (this.refs.input && this.refs.input.value.trim() === '') {
        if (this.refs.placeholderContainer) {
          this.refs.placeholderContainer.style.opacity = '1';
        }
        this.#startPlaceholderAnimation();
      }

      this.dispatchNMSEvent('search:blur', {
        searchId: this.searchId,
        component: this,
      });
    }, 150);
  }

  #toggleClearButton(show) {
    if (this.refs.clearButton) {
      this.refs.clearButton.style.display = show ? 'block' : 'none';
    }
  }

  #navigateResults(direction) {
    this.dispatchNMSEvent('search:navigate', {
      searchId: this.searchId,
      direction: direction,
      component: this,
    });
  }

  #setModalState(isOpen) {
    this.setGlobalData(`search.${this.searchId}.isModalOpen`, isOpen);

    if (this.refs.searchWrapper) {
      if (isOpen) {
        this.refs.searchWrapper.classList.add('nms-search__wrapper--open');
        this.refs.searchWrapper.setAttribute('aria-hidden', 'false');
      } else {
        this.refs.searchWrapper.classList.remove('nms-search__wrapper--open');
        this.refs.searchWrapper.setAttribute('aria-hidden', 'true');
      }
    }

    if (this.refs.searchOverlay) {
      if (isOpen) {
        this.refs.searchOverlay.classList.add('nms-search__overlay--open');
      } else {
        this.refs.searchOverlay.classList.remove('nms-search__overlay--open');
      }
    }

    // Prevent body scroll when modal is open
    if (isOpen) {
      document.body.classList.add('nms-search-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('nms-search-modal-open');
      document.body.style.overflow = '';
    }
  }

  #moveModalToBody() {
    // Store original positions if not already stored
    if (!this.originalParent) {
      this.originalParent = this.refs.searchWrapper?.parentElement;
      this.originalNextSibling = this.refs.searchWrapper?.nextElementSibling;
      this.originalOverlayParent = this.refs.searchOverlay?.parentElement;
      this.originalOverlayNextSibling = this.refs.searchOverlay?.nextElementSibling;
    }

    // Move overlay and wrapper to body to escape any transformed parents
    if (this.refs.searchOverlay && this.refs.searchOverlay.parentElement !== document.body) {
      document.body.appendChild(this.refs.searchOverlay);
    }
    if (this.refs.searchWrapper && this.refs.searchWrapper.parentElement !== document.body) {
      document.body.appendChild(this.refs.searchWrapper);
    }
  }

  #restoreModalPosition() {
    // Only restore if we have original positions stored
    if (this.originalParent && this.refs.searchWrapper) {
      // Restore wrapper
      if (this.originalNextSibling) {
        this.originalParent.insertBefore(this.refs.searchWrapper, this.originalNextSibling);
      } else {
        this.originalParent.appendChild(this.refs.searchWrapper);
      }
    }

    if (this.originalOverlayParent && this.refs.searchOverlay) {
      // Restore overlay
      if (this.originalOverlayNextSibling) {
        this.originalOverlayParent.insertBefore(this.refs.searchOverlay, this.originalOverlayNextSibling);
      } else {
        this.originalOverlayParent.appendChild(this.refs.searchOverlay);
      }
    }
  }

  #handleModalResize() {
    const currentWidth = window.innerWidth;
    const shouldBeInBody = currentWidth <= 1280;
    const isCurrentlyInBody = this.refs.searchWrapper?.parentElement === document.body;

    if (shouldBeInBody && !isCurrentlyInBody) {
      // Screen became smaller, move to body
      this.#moveModalToBody();
    } else if (!shouldBeInBody && isCurrentlyInBody) {
      // Screen became larger, restore to original position
      this.#restoreModalPosition();
    }
  }

  // Public API methods
  closeSearch() {
    this.refs.input?.blur();
    this.#toggleClearButton(false);

    // Dispatch close event for modal/sidebar integration
    this.dispatchNMSEvent('search:close', {
      searchId: this.searchId,
      component: this,
    });
  }

  clearSearch() {
    if (this.refs.input) {
      this.refs.input.value = '';
      this.#handleInput({ target: this.refs.input });
      this.refs.input.focus();
      // Clear the results slot when clearing search
      const resultsContent = this.refs.results?.querySelector('[slot="results"]');
      if (resultsContent) {
        resultsContent.innerHTML = '';
      }
    }
  }

  focus() {
    this.refs.input?.focus();
  }

  blur() {
    this.refs.input?.blur();
  }

  getValue() {
    return this.refs.input?.value || '';
  }

  setValue(value) {
    if (this.refs.input) {
      this.refs.input.value = value;
      this.#handleInput({ target: this.refs.input });
    }
  }

  setLoading(isLoading) {
    this.setGlobalData(`search.${this.searchId}.isLoading`, isLoading);

    if (isLoading) {
      this.showLoading(this.refs.input);
    } else {
      this.hideLoading(this.refs.input);
    }
  }

  // Modal methods
  openModal() {
    // Only move modal elements to body on mobile/tablet screens (up to 1280px)
    if (window.innerWidth <= 1280) {
      this.#moveModalToBody();
    }

    this.#setModalState(true);

    // Stop placeholder animation when modal opens
    this.#stopPlaceholderAnimation();
    if (this.refs.placeholderContainer) {
      this.refs.placeholderContainer.style.opacity = '0';
    }

    // Check for existing results first, then show empty state if needed
    this.#checkForResults();

    // Focus on the modal input after a brief delay
    setTimeout(() => {
      if (this.refs.input) {
        this.refs.input.focus();
      }
    }, 100);

    this.dispatchNMSEvent('search:modal:open', {
      searchId: this.searchId,
      component: this,
    });
  }

  closeModal() {
    this.#setModalState(false);

    // Only restore modal position if we moved it (on mobile/tablet screens)
    if (window.innerWidth <= 1280) {
      this.#restoreModalPosition();
    }

    // Restart placeholder animation when modal closes (if input is empty)
    if (this.refs.input && this.refs.input.value.trim() === '') {
      if (this.refs.placeholderContainer) {
        this.refs.placeholderContainer.style.opacity = '1';
      }
      this.#startPlaceholderAnimation();
    }

    this.dispatchNMSEvent('search:modal:close', {
      searchId: this.searchId,
      component: this,
    });
  }

  toggleModal() {
    const isOpen = this.getGlobalData(`search.${this.searchId}.isModalOpen`);
    if (isOpen) {
      this.closeModal();
    } else {
      this.openModal();
    }
  }

  // Integration with PredictiveSearch
  updateResults(results) {
    this.setGlobalData(`search.${this.searchId}.results`, results);

    // Handle empty state visibility based on results
    this.#handleEmptyState(results);

    this.dispatchNMSEvent('search:results', {
      searchId: this.searchId,
      results: results,
      component: this,
    });
  }

  #handleEmptyState(results) {
    if (!this.refs.searchEmpty) return;

    // Check if results exist and have content
    const hasResults = results && (
      (results.products && results.products.length > 0) ||
      (results.collections && results.collections.length > 0) ||
      (results.articles && results.articles.length > 0) ||
      (results.pages && results.pages.length > 0) ||
      (results.queries && results.queries.length > 0)
    );

    if (hasResults) {
      this.refs.searchEmpty.style.display = 'none';
      this.refs.searchEmpty.setAttribute('aria-hidden', 'true');
    } else {
      this.refs.searchEmpty.style.display = 'block';
      this.refs.searchEmpty.setAttribute('aria-hidden', 'false');
    }
  }

  // Method to handle when search input changes
  #handleSearchInputChange(query) {
    // If there's no query, show empty state
    if (!query || query.trim().length === 0) {
      this.#showEmptyState();
    }
  }

  #showEmptyState() {
    if (this.refs.searchEmpty) {
      this.refs.searchEmpty.style.display = 'block';
      this.refs.searchEmpty.setAttribute('aria-hidden', 'false');
    }
  }

  // Load placeholder texts from data attribute
  #loadPlaceholderTexts() {
    const textsData = this.element.dataset.animatedPlaceholderTexts;
    if (textsData) {
      this.#placeholderTexts = textsData.split('|').filter(text => text.trim() !== '');
    }

    // Fallback to default texts if no data or empty array
    if (this.#placeholderTexts.length === 0) {
      this.#placeholderTexts = ['Sneakers', 'Flats', "Men's", "Slipon's"];
    }
  }

  // Animated placeholder methods
  #initializeAnimatedPlaceholder() {
    if (!this.refs.placeholderText || this.#placeholderTexts.length === 0) {
      return;
    }

    // Set initial placeholder text
    this.#updatePlaceholderText();

    // Start animation cycle
    this.#startPlaceholderAnimation();
  }

  #updatePlaceholderText() {
    if (!this.refs.placeholderText) return;

    const currentText = this.#placeholderTexts[this.#currentPlaceholderIndex];
    this.refs.placeholderText.textContent = currentText;
  }

  #startPlaceholderAnimation() {
    if (this.#placeholderAnimation) {
      clearInterval(this.#placeholderAnimation);
    }

    // Change text every 2 seconds
    this.#placeholderAnimation = setInterval(() => {
      this.#animatePlaceholderTransition();
    }, 2000);
  }

  #stopPlaceholderAnimation() {
    if (this.#placeholderAnimation) {
      clearInterval(this.#placeholderAnimation);
      this.#placeholderAnimation = null;
    }
  }

  #animatePlaceholderTransition() {
    if (!this.refs.placeholderText) {
      return;
    }

    // Add exiting class
    this.refs.placeholderText.classList.add('exiting');

    // After exit animation, change text and enter
    setTimeout(() => {
      // Move to next text
      this.#currentPlaceholderIndex = (this.#currentPlaceholderIndex + 1) % this.#placeholderTexts.length;
      this.#updatePlaceholderText();

      // Remove exiting class and add entering class
      this.refs.placeholderText.classList.remove('exiting');
      this.refs.placeholderText.classList.add('entering');

      // After enter animation, remove entering class
      setTimeout(() => {
        this.refs.placeholderText.classList.remove('entering');
      }, 100);
    }, 100);
  }


  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Clean up mutation observer
    if (this.resultsObserver) {
      this.resultsObserver.disconnect();
      this.resultsObserver = null;
    }

    // Clean up placeholder animation
    this.#stopPlaceholderAnimation();

    // Clean up global reference
    if (window.neemans.search?.[this.searchId]) {
      delete window.neemans.search[this.searchId];
    }

    super.destroy();
  }
}

// REQUIRED: Standard initialization pattern
function initNMSSearch(container = document) {
  if (!window.NMSBaseClass) {
    console.error('NMSSearch: NMSBaseClass required');
    return;
  }

  const elements = container.querySelectorAll('[data-nms-search]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSSearch(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Global initialization function for dynamic content
function reinitNMSSearch(container) {
  initNMSSearch(container);
}

// Auto-initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSSearch);
} else {
  initNMSSearch();
}

// Listen for custom events to reinitialize when new content is added
document.addEventListener('nms:reinit', (event) => {
  const container = event.detail?.container || document;
  initNMSSearch(container);
});

// Listen for theme loading end event (after facet/filter updates)
document.addEventListener('theme:loading:end', () => {
  // Use setTimeout to ensure DOM is fully settled
  setTimeout(() => {
    // Only target the search-page-main component that gets broken by facet updates
    const searchPageMain = document.getElementById('search-page-main');

    if (searchPageMain) {
      // Check if user is currently typing (input has focus and value)
      const searchInput = searchPageMain.querySelector('[data-ref="input"]');
      const isUserTyping = searchInput && document.activeElement === searchInput && searchInput.value.length > 0;

      // If user is actively typing, delay the reinitialization
      if (isUserTyping) {
        // Wait for user to finish typing, then reinitialize
        setTimeout(() => {
          const stillTyping = searchInput && document.activeElement === searchInput;
          if (!stillTyping) {
            reinitializeSearchPageMain(searchPageMain);
          }
        }, 1000);
        return;
      }

      // Safe to reinitialize immediately
      reinitializeSearchPageMain(searchPageMain);
    }

    // Also target the nms-search-flyout-search component that gets broken by facet updates
    const nmsSearchFlyout = document.getElementById('nms-search-flyout-search');

    if (nmsSearchFlyout) {
      // Check if user is currently typing (input has focus and value)
      const searchInput = nmsSearchFlyout.querySelector('[data-ref="input"]');
      const isUserTyping = searchInput && document.activeElement === searchInput && searchInput.value.length > 0;

      // If user is actively typing, delay the reinitialization
      if (isUserTyping) {
        // Wait for user to finish typing, then reinitialize
        setTimeout(() => {
          const stillTyping = searchInput && document.activeElement === searchInput;
          if (!stillTyping) {
            reinitializeNMSSearchFlyout(nmsSearchFlyout);
          }
        }, 1000);
        return;
      }

      // Safe to reinitialize immediately
      reinitializeNMSSearchFlyout(nmsSearchFlyout);
    }
  }, 100);
});

// Helper function to reinitialize search-page-main
function reinitializeSearchPageMain(searchPageMain) {
  // Store current value if any
  const currentInput = searchPageMain.querySelector('[data-ref="input"]');
  const currentQuery = currentInput ? currentInput.value : '';

  // Remove initialization flag
  searchPageMain.removeAttribute('data-initialized');

  // Clean up existing instance
  if (window.neemans.search && window.neemans.search['search-page-main']) {
    if (window.neemans.search['search-page-main'].destroy) {
      window.neemans.search['search-page-main'].destroy();
    }
    delete window.neemans.search['search-page-main'];
  }

  // Reinitialize the component
  const instance = new NMSSearch(searchPageMain);
  instance.init();
  searchPageMain.setAttribute('data-initialized', 'true');

  // Restore previous query if there was one
  if (currentQuery) {
    instance.setValue(currentQuery);
  }
}

// Helper function to reinitialize nms-search-flyout-search
function reinitializeNMSSearchFlyout(nmsSearchFlyout) {
  // Store current value if any
  const currentInput = nmsSearchFlyout.querySelector('[data-ref="input"]');
  const currentQuery = currentInput ? currentInput.value : '';

  // Remove initialization flag
  nmsSearchFlyout.removeAttribute('data-initialized');

  // Clean up existing instance
  if (window.neemans.search && window.neemans.search['nms-search-flyout-search']) {
    if (window.neemans.search['nms-search-flyout-search'].destroy) {
      window.neemans.search['nms-search-flyout-search'].destroy();
    }
    delete window.neemans.search['nms-search-flyout-search'];
  }

  // Reinitialize the component
  const instance = new NMSSearch(nmsSearchFlyout);
  instance.init();
  nmsSearchFlyout.setAttribute('data-initialized', 'true');

  // Restore previous query if there was one
  if (currentQuery) {
    instance.setValue(currentQuery);
  }
}

// Also expose the reinit function globally for manual calls
window.neemans = window.neemans || {};
window.neemans.reinitSearch = reinitNMSSearch;

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSSearch = NMSSearch;
